import DOMPurify from 'dompurify';
import { postSegmentService, postTrialInterestBoomiService } from '../../services/trackingServices';
import { gtagEnvelope } from '../gtagEnvelope';
import { FormData, SendAsyncPostObject, SitecoreTrialContentType } from '../../types';
import { getDataResidencyParam } from '../getDataResidencyParam';
import { getCookie } from '../cookies';
import { checkUserValidity, postTrialFormDataServiceLegacy } from '../../services/sitecoreServices';
import { getFlowType } from '../getFlowType';
import { getEventReason } from '../getExperimentVersion';
import { addLPPlusToUrl } from '../successUrlHandler';
import { facebookCAPIGTMEvent } from '../facebookCAPIGTMEvent';
import { analyticsEvent } from '../analyticsEvent';
import { formatMarketingAttributionValueFromCookieForAMP } from '../formatMarketingAttributionValueFromCookie';
import { useAppStore } from '~/common/store/appStore';
import { MODAL_TYPES } from '~/common/consts';

interface PremiumSubmission {
  data: SitecoreTrialContentType;
  values: SendAsyncPostObject;
  marketingOptOutSeen: boolean;
  contactPermission: boolean;
  signal?: AbortSignal;
}

export const handlePremiumTrialSubmission = async ({
  data,
  values,
  marketingOptOutSeen,
  contactPermission,
  signal,
}: PremiumSubmission) => {
  const { formType, trialLength, segExpQuery, segExpValue, modifier, isLPPlus, app } = useAppStore.getState();
  const flowType = getFlowType(formType);
  const successUrl = isLPPlus ? addLPPlusToUrl(data.successUrl, app) : data.successUrl;

  try {
    gtagEnvelope('event', 'cta_submit_click', {
      cta_name: `Start My Free ${trialLength}-Day Trial`,
      cta_url: `${window.location.origin}${window.location.pathname}${successUrl}`,
      cta_position: 'Trial Form',
    });

    if (modifier !== MODAL_TYPES.UNIFIED) {
      await postSegmentService({
        event: 'premium_trial_form_clicked',
        properties: {
          reason: getEventReason(),
          ...formatMarketingAttributionValueFromCookieForAMP(),
        },
      });
    }

    const userEmail = DOMPurify.sanitize(values.email.toString().toLowerCase());

    // gather form data
    const formData: FormData = {
      email: userEmail,
      firstName: '',
      lastName: '',
      jobTitle: '',
      companyName: '',
      companySize: '',
      phoneNumber: '',
      trialSku: data.sku,
      marketingOptOutShown: marketingOptOutSeen,
      contactPermission,
      clientIp: DOMPurify.sanitize(window.akCIp),
      language: DOMPurify.sanitize(getCookie('lang')) || 'en_US',
      trialLength: trialLength || 30,
      dataResidency: getDataResidencyParam(),
      flowType,
      formType,
      affiliateClickId: DOMPurify.sanitize(getCookie('clickid')),
      segExpQuery,
      segExpValue,
    };
    const bossInvalid = await checkUserValidity(
      {
        email: userEmail,
        trialLength,
        trialSku: data.sku,
      },
      signal
    );
    if (signal?.aborted) return bossInvalid;

    if (!bossInvalid.success) {
      return {
        success: false,
        code: bossInvalid.code,
        correctedEmail: bossInvalid.correctedEmail,
      };
    }

    gtagEnvelope('event', 'trial_success_premium', {});
    facebookCAPIGTMEvent({
      contentId: 'lastpass_premium',
      email: formData.email,
    });
    await analyticsEvent(formData.email);
    await postTrialInterestBoomiService({
      email: userEmail,
      contactPermission,
      trialSku: data.sku,
      programName: data.inputs?.programName?.defaultValue,
      programID: data.inputs?.programID?.defaultValue,
      isLPPlus,
    });
    await postTrialFormDataServiceLegacy(formData, successUrl);
  } catch (error: any) {
    return {
      success: false,
      code: '500',
      errorMessage: `Premium submit error: ${error.message}`,
    };
  }
};
